/**
 * NC — free gift controller.
 * Keeps the cart consistent with the gift rules configured in
 * Theme settings → "Cadou & livrare gratuită":
 *  - adds the gift automatically (mode "auto") or lets the customer pick one (mode "choice")
 *  - removes the gift when the cart drops below the threshold or the campaign is over
 *  - keeps a single gift line with quantity 1
 * The gift is made free at checkout by an automatic "Buy X get Y" discount in Shopify.
 */
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

const PROP = '_nc_gift';
const SOURCE = 'nc-gift';
const TZ = 'Europe/Bucharest';
const DECLINED_KEY = 'nc-gift-declined';
const HAD_KEY = 'nc-gift-had';

const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

let busy = false;
let queued = false;

/* ---------- helpers ---------- */

function storage(key, value) {
  try {
    if (value === undefined) return sessionStorage.getItem(key);
    if (value === null) sessionStorage.removeItem(key);
    else sessionStorage.setItem(key, value);
  } catch (e) {
    return null;
  }
  return null;
}

function tzOffset(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: TZ,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(date);
  const v = {};
  parts.forEach((p) => (v[p.type] = p.value));
  return Date.UTC(+v.year, +v.month - 1, +v.day, +v.hour, +v.minute, +v.second) - date.getTime();
}

/** "2026-10-05 23:59" (Romanian time) → timestamp, or null */
function parseRoTime(str, endOfDay) {
  if (!str) return null;
  const m = String(str).trim().match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{2}))?/);
  if (!m) return null;
  const h = m[4] ? +m[4] : endOfDay ? 23 : 0;
  const mi = m[5] ? +m[5] : endOfDay ? 59 : 0;
  const guess = Date.UTC(+m[1], +m[2] - 1, +m[3], h, mi, 0);
  return guess - tzOffset(new Date(guess));
}

function readConfig() {
  const el = document.querySelector('script[data-nc-gift-config]');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch (e) {
    return null;
  }
}

function isActive(cfg) {
  if (!cfg || !cfg.enabled || !cfg.min || !cfg.variants || !cfg.variants.length) return false;
  const now = Date.now();
  const start = parseRoTime(cfg.start, false);
  const end = parseRoTime(cfg.end, true);
  if (start && now < start) return false;
  if (end && now > end) return false;
  return true;
}

const isGift = (item) => item.properties && item.properties[PROP];

function sectionIds() {
  const ids = new Set();
  document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
    if (el.dataset.sectionId) ids.add(el.dataset.sectionId);
  });
  return Array.from(ids);
}

async function post(path, body) {
  const res = await fetch(`${root}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok || data.status) throw new Error(data.description || data.message || 'Cart error');
  return data;
}

async function getCart() {
  const res = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
  return res.json();
}

/** Re-render cart drawer / cart page and the cart bubble. */
async function publish(sections) {
  const cart = await getCart();
  document.dispatchEvent(
    new CartUpdateEvent(cart, SOURCE, {
      itemCount: cart.item_count,
      source: SOURCE,
      sections: sections || {},
    })
  );
}

function giftProperties(cfg) {
  const props = { [PROP]: '1' };
  if (cfg.propLabel && cfg.propValue) props[cfg.propLabel] = cfg.propValue;
  return props;
}

/* ---------- core ---------- */

async function sync() {
  if (busy) {
    queued = true;
    return;
  }
  busy = true;

  try {
    const cfg = readConfig();
    if (!cfg) return;

    const cart = await getCart();
    const gifts = cart.items.filter(isGift);
    const eligible = cart.items.filter((i) => !isGift(i)).reduce((sum, i) => sum + i.final_line_price, 0);
    const allowed = new Set(cfg.variants.map((v) => v.id));
    const unlocked = isActive(cfg) && eligible >= cfg.min;

    // Customer removed the auto-added gift → respect it for this session.
    if (unlocked && cfg.mode === 'auto' && storage(HAD_KEY) === '1' && gifts.length === 0) {
      storage(DECLINED_KEY, '1');
    }
    if (!unlocked) storage(DECLINED_KEY, null);

    const updates = {};
    gifts.forEach((g, i) => {
      if (!unlocked || i > 0 || !allowed.has(g.variant_id)) updates[g.key] = 0;
      else if (g.quantity !== 1) updates[g.key] = 1;
    });

    const keep = gifts.filter((g) => updates[g.key] !== 0);

    if (Object.keys(updates).length) {
      const res = await post('cart/update.js', { updates, sections: sectionIds().join(',') });
      storage(HAD_KEY, keep.length ? '1' : null);
      await publish(res.sections);
      return;
    }

    if (unlocked && !keep.length && cfg.mode === 'auto' && storage(DECLINED_KEY) !== '1') {
      const variant = cfg.variants.find((v) => v.available);
      if (variant) {
        await addGift(variant.id, cfg, []);
        return;
      }
    }

    storage(HAD_KEY, keep.length ? '1' : null);
    togglePicker(unlocked && !keep.length && storage(DECLINED_KEY) === '1');
  } catch (e) {
    console.error('[nc-gift]', e);
  } finally {
    busy = false;
    if (queued) {
      queued = false;
      sync();
    }
  }
}

async function addGift(variantId, cfg, removeKeys) {
  if (removeKeys.length) {
    const updates = {};
    removeKeys.forEach((k) => (updates[k] = 0));
    await post('cart/update.js', { updates });
  }
  const res = await post('cart/add.js', {
    items: [{ id: variantId, quantity: 1, properties: giftProperties(cfg) }],
    sections: sectionIds().join(','),
  });
  storage(HAD_KEY, '1');
  storage(DECLINED_KEY, null);
  await publish(res.sections);
}

/** In "auto" mode the picker is only a fallback after the customer removed the gift. */
function togglePicker(show) {
  document.querySelectorAll('[data-nc-gift-picker][data-auto-only]').forEach((el) => {
    el.hidden = !show;
  });
}

/* ---------- events ---------- */

document.addEventListener('click', async (event) => {
  const btn = event.target instanceof Element ? event.target.closest('[data-nc-gift-add]') : null;
  if (!btn) return;
  event.preventDefault();

  const card = btn.closest('.nc-gift');
  const input = card && card.querySelector('[data-nc-gift-variant]');
  const variantId = input ? parseInt(input.value, 10) : 0;
  const cfg = readConfig();
  if (!variantId || !cfg || busy) return;

  busy = true;
  btn.setAttribute('aria-busy', 'true');
  try {
    const cart = await getCart();
    const current = cart.items.filter(isGift);
    if (current.length === 1 && current[0].variant_id === variantId) return;
    await addGift(
      variantId,
      cfg,
      current.map((g) => g.key)
    );
  } catch (e) {
    console.error('[nc-gift]', e);
  } finally {
    btn.removeAttribute('aria-busy');
    busy = false;
  }
});

document.addEventListener(ThemeEvents.cartUpdate, (event) => {
  const detail = event.detail || {};
  if (detail.sourceId === SOURCE || (detail.data && detail.data.source === SOURCE)) return;
  // let the theme finish re-rendering the cart first
  setTimeout(sync, 150);
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) sync();
});
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => sync());
else sync();
